import { computeDocumentDiffChanges } from '@/components/tiptap/shared/diff-decorations/compute-document-diff';
import { DIFF_PLUGIN_KEY } from '@/components/tiptap/shared/diff-decorations/create-plugin';
import { isEditorLive } from '@/components/tiptap/shared/safe-editor';
import styles from '@/components/tiptap/shared/styles/index.module.scss';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import { useIsProblemBasedChartingEnabled } from '@/features/flags';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { cn } from '@/lib/cn';
import { changeTintClassName, removalTintClassName } from '@heidi/agents';
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { EditorContent, type Editor } from '@tiptap/react';
import { useEffect, useMemo } from 'react';

export interface DraftNoteBodyProps {
  markdown: string;
  isStreaming?: boolean;
  /** Text to tint against: what `markdown` adds is tinted; removals stay hidden. */
  tintAgainst?: string;
  /** The whole body is text this edit removes. */
  removal?: boolean;
}

// The word diff runs on the main thread; past the cap the personalisation diff
// uses (`MAX_PERSONALISATION_DIFF_CONTENT_LENGTH`), the body renders untinted.
export const MAX_TINT_CONTENT_LENGTH = 100_000;

// The editor loads its list extensions under the problem-based-charting
// flag, so lists parse only then, as on the Note tab (`NoteTabPane`).
const toHtml = (markdown: string, lists: boolean): string =>
  markdown
    ? simpleMarkdownToHtml({
        markdown: markdown.replace(/\n{3,}/g, '\n\n'),
        options: { lists },
      })
    : '';

/**
 * Tints what the editor's document adds over `beforeHtml`, both parsed with the
 * editor's own schema so they segment alike (as the edit-history diff does), by
 * whole words as the routine card does. Deletions are skipped: the card reads
 * as the text that will be saved.
 */
const removalDecorations = (doc: Editor['state']['doc']): Decoration[] => {
  const marks: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText || node.nodeSize === 0) return;
    marks.push(
      Decoration.inline(pos, pos + node.nodeSize, {
        class: removalTintClassName,
        'data-change': '',
      })
    );
  });
  return marks;
};

const useTint = (
  editor: Editor | null,
  beforeHtml: string | undefined,
  html: string,
  removal: boolean
) => {
  useEffect(() => {
    if (!isEditorLive(editor, 'DraftNoteBody.tint')) return;
    let decorations = DecorationSet.empty;
    if (removal) {
      decorations = DecorationSet.create(
        editor.state.doc,
        removalDecorations(editor.state.doc)
      );
    } else if (
      beforeHtml !== undefined &&
      Math.max(beforeHtml.length, html.length) <= MAX_TINT_CONTENT_LENGTH
    ) {
      const holder = document.createElement('div');
      holder.innerHTML = beforeHtml;
      // Parsed as `useEditorContentSync` applies the shown text, so a run
      // of spaces both texts share segments alike instead of reading as added.
      const before = ProseMirrorDOMParser.fromSchema(editor.schema).parse(holder, {
        preserveWhitespace: 'full',
      });
      decorations = DecorationSet.create(
        editor.state.doc,
        computeDocumentDiffChanges(before, editor.state.doc, 'word').flatMap((change) =>
          change.type === 'delete'
            ? []
            : [
                Decoration.inline(change.from, change.to, {
                  class: changeTintClassName,
                  'data-change': '',
                }),
              ]
        )
      );
    }
    editor.view.dispatch(editor.state.tr.setMeta(DIFF_PLUGIN_KEY, decorations));
  }, [editor, beforeHtml, html, removal]);
};

/**
 * Read-only scribe-note TipTap preview. Same editor + ProseMirror styles as
 * Ask Heidi's ``UserNotesBlock`` — the Work draft card cannot import Chat, so
 * this is the shared render path.
 */
export const DraftNoteBody = ({
  markdown,
  isStreaming = false,
  tintAgainst,
  removal = false,
}: DraftNoteBodyProps) => {
  const lists = useIsProblemBasedChartingEnabled();
  const htmlContent = useMemo(() => toHtml(markdown, lists), [markdown, lists]);
  const beforeHtml = useMemo(
    () =>
      tintAgainst === undefined || isStreaming ? undefined : toHtml(tintAgainst, lists),
    [tintAgainst, isStreaming, lists]
  );

  const { editor } = useBlockEditorMarkdown({
    content: htmlContent,
    editable: false,
    placeholder: '',
    isStreaming,
    surface: 'document',
  });
  useTint(editor, beforeHtml, htmlContent, removal && !isStreaming);

  if (!htmlContent) return null;

  return (
    <EditorContent
      editor={editor}
      className={cn(styles.ProseMirror, '[&_.ProseMirror]:outline-none')}
    />
  );
};
